import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Building2, Plus, Users, ClipboardList, BarChart3, LogOut,
  ChevronRight, CheckCircle, AlertCircle, Briefcase, Crown, Zap,
  Star, PieChart, Trophy, Search, ArrowUpRight, ExternalLink,
  FileText, Settings, Menu, X, Grid3X3, LayoutDashboard, CreditCard, UserPlus, Mail, Calendar, ShieldCheck
} from 'lucide-react';
import axios from 'axios';
import { CompanyBrand } from '../components/company/CompanyBrand';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CompanyDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [nationalChallengePrice, setNationalChallengePrice] = useState(9999);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('company_token');
    const company = localStorage.getItem('company_data');
    
    if (!token) {
      navigate('/company/auth');
      return;
    }
    
    if (company) {
      setCompanyData(JSON.parse(company));
    }
    
    fetchDashboard(token);
    fetchPricing();
  }, [navigate]);

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/api/public/company-pricing`);
      if (res.data.pricing?.national_challenge_price) {
        setNationalChallengePrice(res.data.pricing.national_challenge_price);
      }
    } catch (err) {
      console.error('Failed to fetch pricing');
    }
  };

  const fetchDashboard = async (token) => {
    try {
      const res = await axios.get(`${API}/api/company/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(res.data);
      setCompanyData(res.data.company);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('company_token');
        localStorage.removeItem('company_data');
        navigate('/company/auth');
      }
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('company_token');
    localStorage.removeItem('company_data');
    navigate('/company/auth');
    toast.success('Logged out successfully');
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.round(seconds / 60);
    return `${mins} min`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f3f2ef]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#0a66c2] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-[#666666]">Loading Dashboard...</span>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  // Navigation items for sidebar
  const navItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/company/dashboard', active: true },
    { label: 'Job Postings', icon: Briefcase, path: '/company/jobs' },
    { label: 'Assessments', icon: ClipboardList, path: '/company/assessments' },
    { label: 'Hiring Challenges', icon: Trophy, path: '/company/hiring-challenges' },
    { label: 'Enterprise', icon: Crown, path: '/company/enterprise' },
    { label: 'Events', icon: Calendar, path: '/company/events' },
    { label: 'Candidate Pool', icon: UserPlus, path: '/company/candidates' },
    { label: 'Email Center', icon: Mail, path: '/company/email-center' },
    { label: 'Talent Search', icon: Search, path: '/company/talent-search' },
    { label: 'Shortlisted', icon: Star, path: '/company/shortlisted' },
    { label: 'Verified Talent', icon: ShieldCheck, path: '/company/verified-shortlist' },
    { label: 'Analytics', icon: BarChart3, path: '/company/analytics' },
    { label: 'Subscription', icon: CreditCard, path: '/company/subscription' },
    { label: 'Settings & Company Profile', icon: Settings, path: '/company/profile' },
  ];

  return (
    <div className="min-h-screen flex bg-[#f3f2ef]">
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
        w-72 lg:w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-out
        lg:transform-none h-screen overflow-y-auto
        ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 px-5 flex items-center justify-between border-b border-gray-200 sticky top-0 bg-white z-10">
          <Link to="/company" className="flex items-center gap-3">
            <CompanyBrand />
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)} 
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg -mr-2"
            data-testid="close-sidebar-btn"
          >
            <X size={20} className="text-[#666666]" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3">
          <p className="text-xs font-semibold text-[#666666] uppercase tracking-wider px-3 mb-2">Navigation</p>
          <div className="space-y-0.5">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all
                  ${item.active 
                    ? 'bg-[#e8f4f8] text-[#0a66c2] font-medium' 
                    : 'text-[#666666] hover:bg-gray-100 hover:text-[#191919]'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={18} strokeWidth={item.active ? 2.5 : 2} />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-[#b24020] hover:bg-red-50 rounded-lg transition-colors"
            data-testid="company-logout-btn"
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg -ml-2"
              data-testid="open-sidebar-btn"
            >
              <Menu size={22} className="text-[#666666]" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-[#191919]">
                Dashboard
              </h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Link 
              to="/company/assessments/create"
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#0a66c2] text-white text-sm font-medium rounded-full hover:bg-[#004182] transition-colors"
              data-testid="header-create-assessment-btn"
            >
              <Plus size={16} />
              <span className="hidden md:inline">New Assessment</span>
            </Link>
            <Link 
              to="/company/assessments/create"
              className="sm:hidden p-2 bg-[#0a66c2] text-white rounded-full hover:bg-[#004182] transition-colors"
              data-testid="header-create-assessment-btn-mobile"
            >
              <Plus size={20} />
            </Link>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
          {/* Welcome Banner */}
          <div className="mb-6 sm:mb-8">
            <p className="text-xs font-semibold text-[#666666] uppercase tracking-wider mb-1">Overview</p>
            <h2 className="text-xl sm:text-2xl font-semibold text-[#191919]">
              Welcome back, {companyData?.company_name || 'Company'}
            </h2>
            <p className="text-[#666666] text-sm mt-1">Here's your hiring activity at a glance</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-total-assessments">
              <p className="text-xs font-medium text-[#666666] uppercase tracking-wider mb-2">Assessments</p>
              <p className="text-2xl sm:text-3xl font-bold text-[#191919]">
                {stats.total_assessments || 0}
              </p>
              <p className="text-xs sm:text-sm text-[#057642] mt-1 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#057642]" />
                {stats.active_assessments || 0} active
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-candidates-invited">
              <p className="text-xs font-medium text-[#666666] uppercase tracking-wider mb-2">Invited</p>
              <p className="text-2xl sm:text-3xl font-bold text-[#191919]">
                {stats.total_candidates_invited || 0}
              </p>
              <p className="text-xs sm:text-sm text-[#666666] mt-1">Candidates</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-candidates-completed">
              <p className="text-xs font-medium text-[#666666] uppercase tracking-wider mb-2">Completed</p>
              <p className="text-2xl sm:text-3xl font-bold text-[#191919]">
                {stats.total_candidates_completed || 0}
              </p>
              <p className="text-xs sm:text-sm text-[#666666] mt-1">Assessments</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-completion-rate">
              <p className="text-xs font-medium text-[#666666] uppercase tracking-wider mb-2">Rate</p>
              <p className="text-2xl sm:text-3xl font-bold text-[#0a66c2]">
                {stats.completion_rate || 0}%
              </p>
              <p className="text-xs sm:text-sm text-[#666666] mt-1">Completion</p>
            </div>
          </div>

          {/* Quick Actions Grid */}
          <div className="mb-6 sm:mb-8">
            <p className="text-xs font-semibold text-[#666666] uppercase tracking-wider mb-3 sm:mb-4">Quick Actions</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              
              {/* Post Job */}
              <Link 
                to="/company/jobs"
                className="group bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md hover:border-[#0a66c2]/30 transition-all flex items-start justify-between"
                data-testid="manage-jobs-btn"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#f3f2ef] rounded-lg flex items-center justify-center group-hover:bg-[#e8f4f8] transition-colors flex-shrink-0">
                    <Briefcase size={20} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#191919] text-sm sm:text-base">Job Postings</h3>
                    <p className="text-xs sm:text-sm text-[#666666] mt-0.5">Post jobs on CodeArena</p>
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors flex-shrink-0 mt-1" />
              </Link>

              {/* Create Assessment - Highlighted */}
              <Link 
                to="/company/assessments/create"
                className="group bg-[#0a66c2] rounded-xl p-4 sm:p-5 hover:bg-[#004182] transition-all flex items-start justify-between"
                data-testid="create-assessment-btn"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Plus size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm sm:text-base">Create Assessment</h3>
                    <p className="text-xs sm:text-sm text-white/80 mt-0.5">Build new tests</p>
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-white/60 group-hover:text-white transition-colors flex-shrink-0 mt-1" />
              </Link>

              {/* Hiring Challenges */}
              <Link 
                to="/company/hiring-challenges"
                className="group bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md hover:border-[#0a66c2]/30 transition-all flex items-start justify-between"
                data-testid="hiring-challenges-btn"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#f3f2ef] rounded-lg flex items-center justify-center group-hover:bg-[#e8f4f8] transition-colors flex-shrink-0">
                    <Trophy size={20} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#191919] text-sm sm:text-base">Hiring Challenges</h3>
                    <p className="text-xs sm:text-sm text-[#666666] mt-0.5">Coding competitions</p>
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors flex-shrink-0 mt-1" />
              </Link>

              {/* Enterprise Hosting */}
              <Link 
                to="/company/enterprise"
                className="group bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md hover:border-[#b24020]/30 transition-all flex items-start justify-between relative overflow-hidden"
                data-testid="enterprise-hosting-btn"
              >
                <div className="flex items-start gap-3 sm:gap-4 relative z-10">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#fff7ed] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Crown size={20} className="text-[#b24020]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#191919] text-sm sm:text-base flex items-center gap-2 flex-wrap">
                      Enterprise
                      <span className="text-[10px] px-2 py-0.5 bg-[#b24020] text-white font-semibold rounded-full">Premium</span>
                    </h3>
                    <p className="text-xs sm:text-sm text-[#666666] mt-0.5">National competitions</p>
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-[#666666] group-hover:text-[#b24020] transition-colors flex-shrink-0 mt-1 relative z-10" />
              </Link>

              {/* Talent Search */}
              <Link 
                to="/company/talent-search"
                className="group bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md hover:border-[#0a66c2]/30 transition-all flex items-start justify-between"
                data-testid="talent-search-btn"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#f3f2ef] rounded-lg flex items-center justify-center group-hover:bg-[#e8f4f8] transition-colors flex-shrink-0">
                    <Search size={20} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#191919] text-sm sm:text-base">Talent Search</h3>
                    <p className="text-xs sm:text-sm text-[#666666] mt-0.5">Find by skills</p>
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors flex-shrink-0 mt-1" />
              </Link>

              {/* Shortlisted Candidates */}
              <Link 
                to="/company/shortlisted"
                className="group bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md hover:border-[#0a66c2]/30 transition-all flex items-start justify-between"
                data-testid="shortlisted-candidates-btn"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#f3f2ef] rounded-lg flex items-center justify-center group-hover:bg-[#e8f4f8] transition-colors flex-shrink-0">
                    <Star size={20} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#191919] text-sm sm:text-base">Shortlisted</h3>
                    <p className="text-xs sm:text-sm text-[#666666] mt-0.5">Top talent pipeline</p>
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors flex-shrink-0 mt-1" />
              </Link>

            </div>
          </div>

          {/* Bottom Grid - Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            
            {/* Recent Assessments */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Recent</p>
                  <h3 className="font-semibold text-[#191919]">Assessments</h3>
                </div>
                <Link 
                  to="/company/assessments" 
                  className="text-sm text-[#0a66c2] hover:underline flex items-center gap-1"
                  data-testid="view-all-assessments-link"
                >
                  View All <ExternalLink size={14} />
                </Link>
              </div>
              <div className="divide-y divide-gray-100">
                {dashboardData?.recent_assessments?.length > 0 ? (
                  dashboardData.recent_assessments.slice(0, 4).map(assessment => (
                    <Link 
                      key={assessment.id}
                      to={`/company/assessments/${assessment.id}`}
                      className="px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between hover:bg-[#f3f2ef] transition-colors"
                      data-testid={`assessment-${assessment.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <h4 className="font-medium text-[#191919] text-sm truncate">{assessment.title}</h4>
                        <p className="text-xs text-[#666666] mt-0.5">
                          {assessment.total_mcqs} MCQ · {assessment.total_coding_problems} Coding · {assessment.duration_minutes}m
                        </p>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 ml-3">
                        <div className="text-right hidden sm:block">
                          <div className="flex items-center gap-1.5 text-xs">
                            {assessment.is_active ? (
                              <span className="flex items-center gap-1 text-[#057642] font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#057642]" /> Active
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-[#666666]">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#666666]" /> Inactive
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-[#666666] mt-0.5">
                            {assessment.candidates_completed}/{assessment.candidates_invited}
                          </p>
                        </div>
                        <ChevronRight size={16} className="text-[#666666]" />
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="px-4 sm:px-5 py-10 sm:py-12 text-center">
                    <ClipboardList className="mx-auto text-gray-300 mb-3" size={36} />
                    <p className="text-[#666666] text-sm">No assessments yet</p>
                    <Link 
                      to="/company/assessments/create" 
                      className="text-sm text-[#0a66c2] hover:underline mt-2 inline-block"
                      data-testid="create-first-assessment-link"
                    >
                      Create your first assessment
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Completions */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 sm:px-5 py-4 border-b border-gray-100">
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Recent</p>
                <h3 className="font-semibold text-[#191919]">Candidate Completions</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {dashboardData?.recent_candidates?.length > 0 ? (
                  dashboardData.recent_candidates.slice(0, 4).map(candidate => (
                    <div 
                      key={candidate.id} 
                      className="px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between hover:bg-[#f3f2ef] transition-colors"
                      data-testid={`candidate-${candidate.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 bg-[#0a66c2] rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-white">
                            {(candidate.candidate_name || candidate.email || '?')[0].toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-medium text-[#191919] truncate text-sm">
                            {candidate.candidate_name || candidate.email?.split('@')[0] || 'Candidate'}
                          </h4>
                          <p className="text-xs text-[#666666] truncate">{candidate.assessment_title}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <div className={`text-lg font-bold ${candidate.passed ? 'text-[#057642]' : 'text-[#b24020]'}`}>
                          {candidate.percentage}%
                        </div>
                        <p className="text-[10px] text-[#666666]">
                          {formatTime(candidate.time_taken_seconds)}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 sm:px-5 py-10 sm:py-12 text-center">
                    <Users className="mx-auto text-gray-300 mb-3" size={36} />
                    <p className="text-[#666666] text-sm">No candidates completed yet</p>
                    <p className="text-xs text-[#666666] mt-1">Invite candidates to your assessments</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Features Row */}
          <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <Link 
              to="/company/analytics"
              className="group bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-[#0a66c2]/30 transition-all flex items-center gap-3"
              data-testid="view-analytics-btn"
            >
              <div className="w-10 h-10 bg-[#f3f2ef] rounded-lg flex items-center justify-center group-hover:bg-[#e8f4f8] transition-colors flex-shrink-0">
                <BarChart3 size={18} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors" />
              </div>
              <div className="min-w-0">
                <h4 className="font-medium text-[#191919] text-sm">Analytics</h4>
                <p className="text-xs text-[#666666]">View reports</p>
              </div>
            </Link>

            <Link 
              to="/company/interview-insights"
              className="group bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-[#0a66c2]/30 transition-all flex items-center gap-3"
              data-testid="interview-insights-btn"
            >
              <div className="w-10 h-10 bg-[#f3f2ef] rounded-lg flex items-center justify-center group-hover:bg-[#e8f4f8] transition-colors flex-shrink-0">
                <PieChart size={18} className="text-[#666666] group-hover:text-[#0a66c2] transition-colors" />
              </div>
              <div className="min-w-0">
                <h4 className="font-medium text-[#191919] text-sm">Interview Insights</h4>
                <p className="text-xs text-[#666666]">Hiring funnel</p>
              </div>
            </Link>

            <Link 
              to="/company/hiring-challenges?create=national"
              className="group bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-[#b24020]/30 transition-all flex items-center gap-3"
              data-testid="national-challenge-btn"
            >
              <div className="w-10 h-10 bg-[#fff7ed] rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap size={18} className="text-[#b24020]" />
              </div>
              <div className="min-w-0">
                <h4 className="font-medium text-[#191919] text-sm flex items-center gap-2">
                  National Challenge
                  <span className="text-[10px] px-1.5 py-0.5 bg-[#b24020] text-white font-semibold rounded">₹{nationalChallengePrice.toLocaleString()}</span>
                </h4>
                <p className="text-xs text-[#666666]">Go national</p>
              </div>
            </Link>
          </div>

        </div>
      </main>
    </div>
  );
};

export default CompanyDashboard;
